/**
 * The device's cryptographic identity. Instead of a forgeable random UUID kept in
 * localStorage, each browser holds a NON-EXTRACTABLE ECDSA P-256 key pair whose
 * private half never leaves the platform — the public key and its RFC 7638 JWK
 * thumbprint become the device's stable, server-verifiable identity, and the private
 * key is what a later proof-of-possession step signs challenges with.
 *
 * Why IndexedDB and not localStorage: IndexedDB structured-clones a live `CryptoKey`
 * and PRESERVES `extractable: false`, so the private key is stored as an opaque handle
 * that JS can `sign` with but can never read back or serialise. localStorage can only
 * hold strings, which would force us to expose key material — the exact thing we are
 * avoiding.
 *
 * The key is generated once, on the first authenticated load, and reused forever after.
 *
 * Lives in the styleguide (not the shell) because the code that proves possession of this
 * key — the DPoP interceptor in ./dpop — runs at chokepoints spread across the shell and
 * two pilets, and pilets can only share code through the styleguide. The stored key is a
 * single IndexedDB record for the origin, so every bundle that imports this reaches the
 * same key regardless of module-scope memoisation.
 */

/** Fixed IndexedDB coordinates for the single stored key pair. */
const DB_NAME = 'medialib'
const STORE_NAME = 'keyval'
const DEVICE_KEY = 'device-key'

/** The key's public identity, computed once and cached for the session. */
export interface DeviceKeyIdentity {
  /** The public key as a JWK — sent to the Device API at enrollment. */
  pubJwk: JsonWebKey;
  /** RFC 7638 thumbprint of the public JWK, base64url — the device's stable id. */
  thumbprint: string;
}

/**
 * Minimal promise-based IndexedDB key/value access — just enough to persist and reload
 * one `CryptoKeyPair`. Kept in-house (≈15 lines) rather than pulling in `idb-keyval`:
 * a single fixed key on a single store doesn't warrant a dependency in the shared
 * bundle. The object store is created lazily on first open.
 */
function withStore<T> (mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1)
    open.onupgradeneeded = () => open.result.createObjectStore(STORE_NAME)
    open.onerror = () => reject(open.error)
    open.onsuccess = () => {
      const db = open.result
      const tx = db.transaction(STORE_NAME, mode)
      const req = run(tx.objectStore(STORE_NAME))
      req.onsuccess = () => resolve(req.result as T)
      req.onerror = () => reject(req.error)
      tx.oncomplete = () => db.close()
    }
  })
}

/** Read a value by key, or `undefined` when absent. */
function idbGet<T> (key: string): Promise<T | undefined> {
  return withStore<T | undefined>('readonly', store => store.get(key))
}

/** Store `value` under `key` (structured-cloned, so a `CryptoKey` keeps its handle). */
function idbPut (key: string, value: unknown): Promise<void> {
  return withStore<IDBValidKey>('readwrite', store => store.put(value, key)).then(() => undefined)
}

/**
 * The device key pair for this browser: the persisted one when present, otherwise a
 * freshly generated non-extractable ECDSA P-256 pair, stored for next time. Memoised so
 * concurrent callers within a session share one generation.
 */
let keyPairPromise: Promise<CryptoKeyPair> | null = null
export function getOrCreateDeviceKey (): Promise<CryptoKeyPair> {
  if (keyPairPromise === null) {
    keyPairPromise = (async () => {
      const existing = await idbGet<CryptoKeyPair>(DEVICE_KEY)
      // A stored value is only usable if it round-tripped as a real key pair (older
      // records or a partial write could be anything) — regenerate otherwise.
      if (existing?.privateKey instanceof CryptoKey && existing.publicKey instanceof CryptoKey) {
        return existing
      }
      const pair = await crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' },
        false, // extractable: false — the private key must never become readable JS.
        ['sign', 'verify']
      )
      // Persist the WHOLE pair: the private key is needed to sign later, and IndexedDB
      // keeps its non-extractability across reloads.
      await idbPut(DEVICE_KEY, pair)
      return pair
    })().catch(err => {
      // Let a transient failure (storage blocked, quota) retry on the next call rather
      // than caching a rejection for the life of the session.
      keyPairPromise = null
      throw err
    })
  }
  return keyPairPromise
}

/** base64url (no padding) encode raw bytes — the JOSE encoding for thumbprints. */
function base64url (bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * RFC 7638 JWK thumbprint of an EC public JWK: the required members `{crv, kty, x, y}`
 * only, in lexicographic order, serialised with no whitespace, SHA-256'd, base64url.
 * Any two clients that agree on the public key derive the identical thumbprint.
 */
async function jwkThumbprint (jwk: JsonWebKey): Promise<string> {
  const canonical = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y })
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical))
  return base64url(new Uint8Array(digest))
}

/**
 * The public identity of this browser's device key — its public JWK and thumbprint.
 * Exporting the PUBLIC key as a JWK is permitted even on a non-extractable pair (only
 * the private half is protected). Memoised: both values are stable for the key's life,
 * so we compute them once per session.
 */
let identityPromise: Promise<DeviceKeyIdentity> | null = null
export function getDeviceKeyIdentity (): Promise<DeviceKeyIdentity> {
  if (identityPromise === null) {
    identityPromise = (async () => {
      const { publicKey } = await getOrCreateDeviceKey()
      const pubJwk = await crypto.subtle.exportKey('jwk', publicKey)
      const thumbprint = await jwkThumbprint(pubJwk)
      return { pubJwk, thumbprint }
    })().catch(err => {
      identityPromise = null
      throw err
    })
  }
  return identityPromise
}

/** base64url a UTF-8 string — the encoding for JWS header/payload segments. */
function base64urlText (text: string): string {
  return base64url(new TextEncoder().encode(text))
}

/** What binds a proof to a single request. */
export interface ProofParams {
  /** HTTP method of the request the proof authorises, e.g. `'POST'` (the `htm` claim). */
  htm: string;
  /** Target URI of that request, e.g. `'https://host/api/...'` (the `htu` claim). */
  htu: string;
  /** Server-issued nonce to echo, when the server has demanded one (the `nonce` claim). */
  nonce?: string;
}

/**
 * Build a DPoP-style proof JWS for one request, signed by this browser's device key —
 * the proof-of-possession that lets the backend confirm the caller holds the private key
 * enrolled for the device.
 *
 * - Header `{ typ: 'dpop+jwt', alg: 'ES256', jwk }` carries the PUBLIC JWK (reduced to
 *   its `{crv, kty, x, y}` members, so the server derives the same thumbprint we enrolled
 *   and no key_ops/ext noise rides along).
 * - Payload `{ jti, htm, htu, iat, nonce? }` — a random `jti` and a second-resolution
 *   `iat` make each proof single-use and time-bound; `htm`/`htu` bind it to the request.
 * - Signed with ECDSA P-256 / SHA-256. WebCrypto returns the signature as raw `r‖s`
 *   (IEEE P1363) — already the JOSE ES256 encoding, so there is NO DER unwrapping to do.
 *
 * Returns the compact JWS `base64url(header).base64url(payload).base64url(signature)`.
 */
export async function makeProof (params: ProofParams): Promise<string> {
  const { privateKey } = await getOrCreateDeviceKey()
  const { pubJwk } = await getDeviceKeyIdentity()

  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: { crv: pubJwk.crv, kty: pubJwk.kty, x: pubJwk.x, y: pubJwk.y }
  }
  const payload: Record<string, unknown> = {
    jti: crypto.randomUUID(),
    htm: params.htm,
    htu: params.htu,
    iat: Math.floor(Date.now() / 1000)
  }
  if (params.nonce !== undefined) payload.nonce = params.nonce

  const signingInput = `${base64urlText(JSON.stringify(header))}.${base64urlText(JSON.stringify(payload))}`
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    new TextEncoder().encode(signingInput)
  )
  return `${signingInput}.${base64url(new Uint8Array(signature))}`
}
